import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import client from "../lib/api";
import { BRAND } from "../mock";
import { PropertyForm, EMPTY_PROPERTY, formToPayload } from "../components/PropertyForm";
import { uploadPropertyFile, mediaSrc } from "../lib/media";
import { statusBadge, fmtPrice } from "../lib/catalog";
import { Camera, CheckCircle2, Loader2, X, Languages } from "lucide-react";
import { toast } from "sonner";
import { translate } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export default function SubmitPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(() => (navigator.language || "").startsWith("fr") ? "fr" : "en");
  const [tab, setTab] = useState("submit");
  const [form, setForm] = useState({ ...EMPTY_PROPERTY });
  const [media, setMedia] = useState([]);   // {url, kind, name}
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const t = (s) => translate(lang, s);
  const hdr = { headers: { "X-Submit-Token": token } };

  useEffect(() => {
    window.__SUBMIT_TOKEN__ = token;
    axios.get(`${API}/submit/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(e?.response?.data?.detail || "This link isn't available."));
    return () => { delete window.__SUBMIT_TOKEN__; };
  }, [token]);
  const loadMine = () => axios.get(`${API}/submit/${token}/properties`).then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { if (tab === "mine") loadMine(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    setUploading((n) => n + files.length);
    for (const f of files) {
      try { const r = await uploadPropertyFile(f, "", null, { purpose: "loose", token }); setMedia((m) => [...m, r.media]); }
      catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`); }
      finally { setUploading((n) => n - 1); }
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    const photos = media.filter((m) => m.kind === "image");
    if (!form.propertyType) { toast.error(t("Property type") + " *"); return; }
    if (photos.length < 3) { toast.error(t("Photos (at least 3)")); return; }
    setSaving(true);
    try {
      const payload = { ...formToPayload(form), photos: photos.map((m) => m.url), videos: media.filter((m) => m.kind === "video") };
      delete payload.agentId;
      await client.post(`/submit/${token}/properties`, payload, hdr);
      toast.success(t("Sent! Moroccan Brothers will review it shortly."));
      setForm({ ...EMPTY_PROPERTY }); setMedia([]); setTab("mine");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send"); } finally { setSaving(false); }
  };
  const setStatus = async (p, st) => {
    try { await axios.post(`${API}/submit/${token}/properties/${p.id}/status`, { listingStatus: st }); toast.success(t(st)); loadMine(); } catch (err) { toast.error("Couldn't update"); }
  };

  if (error) return <div className="grid min-h-screen place-items-center bg-background px-6 text-center" data-testid="submit-error"><div><img src={BRAND.logo} alt="" className="mx-auto h-16 object-contain" /><h1 className="mt-6 font-display text-xl text-white">{error}</h1></div></div>;
  if (!info) return <div className="grid min-h-screen place-items-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-red border-t-transparent" /></div>;

  return (
    <div className="min-h-screen bg-background pb-24 text-white" data-testid="submit-page">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-white/[0.06] bg-background/90 px-4 py-3 backdrop-blur">
        <img src={BRAND.logo} alt="Moroccan Brothers" className="h-10 object-contain" />
        <div className="text-right"><p className="text-xs text-muted-foreground">{info.agent.name} · {info.agent.agency}</p>
          <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} data-testid="lang-switch" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green"><Languages className="h-3.5 w-3.5" /> {lang === "fr" ? "English" : "Français"}</button></div>
      </header>
      <div className="mx-auto max-w-xl px-4">
        <div className="mt-4 grid grid-cols-2 gap-1 rounded-xl bg-white/[0.04] p-1">
          <button onClick={() => setTab("submit")} data-testid="tab-submit" className={`rounded-lg py-2 text-sm font-semibold ${tab === "submit" ? "bg-brand-red text-white" : "text-muted-foreground"}`}>{t("Submit a property")}</button>
          <button onClick={() => setTab("mine")} data-testid="tab-mine" className={`rounded-lg py-2 text-sm font-semibold ${tab === "mine" ? "bg-brand-red text-white" : "text-muted-foreground"}`}>{t("My properties")}</button>
        </div>
        {tab === "submit" ? (
          <div className="mt-5">
            <PropertyForm form={form} setForm={setForm} onSubmit={submit} submitting={saving || uploading > 0} hideAgent locations={info} t={t} token={token} submitLabel={t("Send to Moroccan Brothers")}>
              <div>
                <label className="text-xs text-muted-foreground">{t("Photos (at least 3)")} * <span className="text-white">({media.filter((m) => m.kind === "image").length})</span></label>
                <div className="mt-1 grid grid-cols-4 gap-2">
                  {media.map((m) => (
                    <div key={m.url} className="relative aspect-square overflow-hidden rounded-lg bg-black/40">
                      {m.kind === "video" ? <video src={mediaSrc(m.url)} muted className="h-full w-full object-cover" /> : <img src={mediaSrc(m.url)} alt="" className="h-full w-full object-cover" />}
                      <button type="button" onClick={() => setMedia((x) => x.filter((y) => y.url !== m.url))} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <label data-testid="submit-photos-tile" className="grid aspect-square cursor-pointer place-items-center rounded-lg border-2 border-dashed border-white/15 text-center text-[11px] text-muted-foreground">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin text-brand-red" /> : <><Camera className="h-5 w-5 text-brand-red" />{t("Add photos / videos")}</>}
                    <input type="file" accept="image/*,video/*" multiple capture="environment" className="hidden" onChange={pick} data-testid="submit-photos-input" />
                  </label>
                </div>
              </div>
            </PropertyForm>
          </div>
        ) : (
          <div className="mt-5 space-y-3" data-testid="my-properties">
            {mine.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">—</p>}
            {mine.map((p) => (
              <div key={p.id} data-testid={`mine-${p.id}`} className="flex gap-3 rounded-2xl border border-white/[0.07] bg-card/40 p-3">
                <img src={mediaSrc(p.image)} alt="" className="h-20 w-20 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.district} · {fmtPrice(p.listingType === "rental" ? p.rentMad : p.priceMad)}</p>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusBadge(p.listingStatus)}`} data-testid="mine-status">{t(p.listingStatus)}</span>
                  {p.listingStatus === "Rejected" && p.rejectReason && <p className="mt-1 text-[11px] text-muted-foreground">{p.rejectReason}</p>}
                  {!["Sold", "Rented", "Withdrawn", "Rejected"].includes(p.listingStatus) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button onClick={() => setStatus(p, "Sold")} data-testid="mine-sold" className="rounded-lg border border-white/12 px-2 py-1 text-[11px]">{t("Mark as sold")}</button>
                      <button onClick={() => setStatus(p, "Rented")} data-testid="mine-rented" className="rounded-lg border border-white/12 px-2 py-1 text-[11px]">{t("Mark as rented")}</button>
                      <button onClick={() => setStatus(p, "Withdrawn")} data-testid="mine-withdrawn" className="rounded-lg border border-white/12 px-2 py-1 text-[11px]">{t("No longer available")}</button>
                    </div>
                  )}
                </div>
                {p.listingStatus === "Available" && <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-green" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
